import type { CallQuestion, EmergencyCall, Incident, SimState } from "./model";
import { incidentProfiles, patientPlan } from "./incidentProfiles";
export const questionLabels: Record<CallQuestion, string> = {
  opening: "Anruf annehmen",
  where: "Wo ist der Notfall?",
  who: "Wer ruft an?",
  what: "Was ist passiert?",
  how: "Wie geht es den Betroffenen?",
  count: "Wie viele sind betroffen?",
  danger: "Welche Gefahren bestehen?",
};
export const defaultCallSettings = {
  minSeconds: 180,
  maxSeconds: 360,
  maxWaiting: 5,
  scaleWithFleet: false,
};
export function randomValue(s: SimState) {
  s.seed = (Math.imul(s.seed, 1664525) + 1013904223) >>> 0;
  return s.seed / 4294967296;
}
export function nextCallDelay(s: SimState) {
  const c = s.callSettings ?? defaultCallSettings;
  const units = s.vehicles.filter((v) => v.status !== 6 && !v.mutualAid).length;
  const scale = c.scaleWithFleet ? Math.max(0.5, Math.min(3, units / 20)) : 1;
  return (
    Math.max(
      30,
      (c.minSeconds + randomValue(s) * (c.maxSeconds - c.minSeconds)) / scale,
    ) * 1000
  );
}
export function callFromIncident(s: SimState, i: Incident): EmergencyCall {
  const plan = patientPlan(i.profileId!);
  const count = plan?.count ?? 0;
  const intro = [
    "Hallo? Bitte schicken Sie Hilfe.",
    "Guten Tag, ich muss einen Notfall melden.",
    "Sind Sie die Leitstelle? Hier ist etwas passiert.",
    "Bitte helfen Sie uns, ich bin ziemlich aufgeregt.",
  ];
  const name = i.callerName!;
  return {
    id: "notruf-" + i.number + "-" + s.now,
    profileId: i.profileId!,
    callerName: name,
    speakerId: Math.floor(randomValue(s) * 236),
    createdAt: s.now,
    phase: "ringing",
    answered: [],
    address: i.address,
    district: i.district,
    location: [...i.location],
    addressSource: i.addressSource,
    street: i.street,
    houseNumber: i.houseNumber,
    postcode: i.postcode,
    answers: {
      opening: intro[Math.floor(randomValue(s) * intro.length)],
      where:
        "Ich bin in " +
        i.district +
        ", " +
        i.address +
        ". Genau dort brauchen wir Hilfe.",
      who:
        "Mein Name ist " +
        name +
        ". Ich bin hier vor Ort und bleibe am Telefon.",
      what: incidentProfiles[i.profileId!].report,
      how: !count
        ? "Im Moment sehe ich keine verletzten Menschen."
        : plan?.critical
          ? "Mindestens " +
            plan.critical +
            " " +
            (plan.critical === 1 ? "Person wirkt" : "Personen wirken") +
            " sehr schwer betroffen. Bitte kommen Sie schnell."
          : "Die betroffene Person ist ansprechbar, braucht aber Hilfe.",
      count: count
        ? "Ich sehe ungefähr " +
          count +
          " " +
          (count === 1
            ? "betroffene Person."
            : "betroffene Menschen. Mehr kann ich gerade nicht sicher sagen.")
        : "Soweit ich sehen kann, ist niemand verletzt.",
      danger:
        i.type === "brand"
          ? "Es gibt Rauch und Feuer. Ich halte Abstand. Ob noch jemand im Gebäude ist, kann ich nicht sicher sagen."
          : i.type === "vu"
            ? "Es fahren noch andere Fahrzeuge an der Unfallstelle vorbei."
            : i.type === "polizei"
              ? "Ich halte Abstand. Die Lage ist für mich schwer einzuschätzen."
              : "Ich kann keine weiteren Gefahren erkennen. Ich bleibe hier und warte auf Ihre Fragen.",
    },
  };
}
export function findCall(s: SimState, id: string) {
  const c = s.calls?.find((c) => c.id === id);
  if (!c) throw Error("Notruf nicht gefunden.");
  return c;
}
export function answerCall(s: SimState, id: string, question: CallQuestion) {
  const c = findCall(s, id);
  if (c.phase !== "active") throw Error("Zuerst den Notruf annehmen.");
  if (!c.answered.includes(question)) c.answered.push(question);
  return c;
}
